import axios from "axios";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import {
  TableContainer as MuiTableContainer,
  Table as MuiTable,
  TableBody as MuiTableBody,
  TextField as MuiTextField,
  Divider as MuiDivider,
  Grid as MuiGrid,
  Paper as MuiPaper,
  Button as MuiButton,
  IconButton as MuiIconButton,
  Dialog as MuiDialog,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  List as MuiList,
  ListItem as MuiListItem,
  ListItemText as MuiListItemText,
  Typography as MuiTypography,
  Checkbox as MuiCheckbox,
  Tooltip as MuiToolTip,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
} from "@mui/material";
import { Delete as MuiDeleteIcon } from "@mui/icons-material";
import { useFormikContext } from "formik";
import { Theme, useAlerts } from "common";
import {
  GET_STUDIES_URL_BASE,
  TokensLabelMapping,
  TokenType,
} from "app/shared/constants";
import { combineData, sortByTemplateName } from "app/shared/utils";
import ConfirmationDialog from "app/shared/UI/Dialog/ConfirmationDialog";
import RichTextEditor from "./RichTextEditor";
import { templatesConfigStyles } from "./TemplatesConfigStyles";

const headerCell = ["Token", "Value"];

const TemplatesConfiguration = ({ templatesData }) => {
  const { t } = useTranslation();
  const { setAlert } = useAlerts();
  const [openASConfirmation, setOpenASConfirmation] = useState(false);
  const [openASConfirmationIndex, setOpenASConfirmationIndex] = useState(null);

  const { values, errors, handleBlur, touched, setFieldValue } =
    useFormikContext();

  const [openDialogIndex, setOpenDialogIndex] = useState(null);
  const [selectedTempIndex, setSelectedTempIndex] = useState(0);

  const initCombinedData = templatesData.studyTemplates
    ? combineData(templatesData.outreachTemplates, templatesData.studyTemplates)
    : sortByTemplateName(templatesData.outreachTemplates ?? []);

  // function to check if there is an error for a specific tokens value in a study template
  const hasError = (templateIndex, tokenIndex) => {
    return (
      Object.keys(errors).length > 0 &&
      Object.keys(touched).length > 0 &&
      errors?.studyTemplates?.[templateIndex]?.tokens[tokenIndex]?.value &&
      touched?.studyTemplates?.[templateIndex]?.tokens[tokenIndex]?.value
    );
  };

  useEffect(() => {
    const defaultIndex = values.studyTemplates.findIndex(
      (template) => template.isDefault
    );

    setSelectedTempIndex((prev) =>
      prev !== undefined ? prev : defaultIndex !== -1 ? defaultIndex : 0
    );
  }, [values.studyTemplates]);

  const closeCancelASConfirmation = (cancelChoice, template, tempInd) => {
    setOpenASConfirmation(false);

    if (cancelChoice) {
      (async () => {
        try {
          await axios.delete(
            `${GET_STUDIES_URL_BASE}/${values.id}/${template.templateId}`
          );

          const tempID = initCombinedData[tempInd].templateId;
          const ptemplate = templatesData.outreachTemplates.find(
            (item) => item.templateId === tempID
          );

          setFieldValue(`studyTemplates.[${tempInd}]`, ptemplate);
          setFieldValue(`studyTemplates.[${tempInd}].toRemoveInPatch`, true);
        } catch (err) {
          // set error message in AlertContext
          setAlert("error", err.message);
        }
      })();
    }
  };

  return (
    <MuiGrid container size={12}>
      <MuiGrid size={3}>
        <MuiPaper sx={templatesConfigStyles.root}>
          <MuiTypography variant="body" sx={templatesConfigStyles.tempHeading}>
            {t("templateConfigTable.templateCap")}
          </MuiTypography>
          <MuiDivider />
          <MuiList>
            {values.studyTemplates.map((template, tempInd) => (
              <>
                <MuiListItem
                  button
                  selected={selectedTempIndex === tempInd}
                  onClick={(event) => setSelectedTempIndex(tempInd)}
                  sx={{
                    backgroundColor:
                      selectedTempIndex === tempInd ? "#EBEBEB" : "transparent",
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                >
                  <MuiToolTip
                    title={t("templateConfigTable.templateNameToolTip")}
                    arrow
                  >
                    <MuiListItemText
                      primary={template.templateName}
                      slotProps={{
                        primary: {
                          style: { fontSize: "15px" },
                        },
                      }}
                    />
                  </MuiToolTip>

                  {template.getStudyTemp && (
                    <>
                      <MuiToolTip
                        title={t("templateConfigTable.trashToolTip")}
                        arrow
                      >
                        <MuiIconButton
                          onClick={(e) => {
                            if (
                              template.getStudyTemp &&
                              template.initialGetStudyTemp
                            ) {
                              setOpenASConfirmationIndex(tempInd);
                              setOpenASConfirmation(true);
                            } else {
                              setFieldValue(
                                `studyTemplates.[${tempInd}]`,
                                initCombinedData[tempInd]
                              );
                            }
                          }}
                        >
                          <MuiDeleteIcon fontSize="small" />
                        </MuiIconButton>
                      </MuiToolTip>
                    </>
                  )}

                  <MuiToolTip
                    title={t("templateConfigTable.checkBoxToolTip")}
                    arrow
                  >
                    <MuiCheckbox
                      name={`studyTemplates`}
                      key={`studyTemplates.[${tempInd}].isDefault`}
                      id={`studyTemplates.[${tempInd}].isDefault`}
                      color="pepGray"
                      checked={!!template.isDefault}
                      disabled={!template.getStudyTemp}
                      size="small"
                      onChange={() => {
                        return values.studyTemplates.forEach((temp, index) => {
                          if (tempInd !== index) {
                            setFieldValue(
                              `studyTemplates.[${index}].isDefault`,
                              false
                            );
                          } else {
                            setFieldValue(
                              `studyTemplates.[${index}].isDefault`,
                              !values.studyTemplates[index].isDefault
                            );
                          }
                        });
                      }}
                    />
                  </MuiToolTip>
                </MuiListItem>

                {openASConfirmationIndex === tempInd && (
                  <ConfirmationDialog
                    open={openASConfirmation}
                    onClose={(e) =>
                      closeCancelASConfirmation(e, template, tempInd)
                    }
                    message={t("templateConfigTable.deleteStudyConfirmMsg")}
                    okLabel={t("common.yesLabel")}
                    cancelLabel={t("common.noLabel")}
                  />
                )}
              </>
            ))}
          </MuiList>
        </MuiPaper>
      </MuiGrid>

      <MuiGrid
        size={9}
        sx={{
          paddingLeft: "16px",
        }}
      >
        {/* TEMPLATES STARTS */}
        <MuiPaper sx={templatesConfigStyles.templatesConfigRoot}>
          <MuiTableContainer>
            <MuiTable>
              <MuiTableHead sx={templatesConfigStyles.studyTableHead}>
                <MuiTableRow sx={templatesConfigStyles.studyTableRow}>
                  <MuiGrid container>
                    <MuiGrid
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        width: "100%",
                      }}
                    >
                      {headerCell.map((headCell) => {
                        return (
                          <MuiGrid
                            size={6}
                            sx={{
                              color: Theme.palette.common.white,
                              padding: "5px 10px",
                              paddingLeft: headCell === "Token" ? "14px" : "0",
                              fontSize: "15px",
                            }}
                          >
                            {headCell}
                          </MuiGrid>
                        );
                      })}
                    </MuiGrid>
                  </MuiGrid>
                </MuiTableRow>
              </MuiTableHead>

              {/* TABLE BODY */}
              <MuiTableBody>
                {/* selectedTempIndex */}
                {values.studyTemplates.length > 0 ? (
                  values.studyTemplates[selectedTempIndex] &&
                  values.studyTemplates[selectedTempIndex]?.tokens?.map(
                    (perTemplate, tokenIndex) => {
                      return (
                        <MuiGrid key={tokenIndex}>
                          <MuiGrid
                            sx={{
                              display: "flex",
                              justifyContent: "flex-start",
                              padding: "5px",
                            }}
                          >
                            <MuiGrid size={6}>
                              <MuiTypography
                                sx={{
                                  padding: "5px 10px",
                                  display: "flex",
                                  alignItems: "center",
                                  fontSize: "15px",
                                }}
                              >
                                {TokensLabelMapping[perTemplate.name]
                                  ? TokensLabelMapping[perTemplate.name]
                                  : perTemplate.name}
                              </MuiTypography>
                            </MuiGrid>
                            <MuiGrid
                              size={6}
                              sx={{
                                width: "300px",
                              }}
                            >
                              {perTemplate.type === TokenType.NUMBER ? (
                                <NumericFormat
                                  fullWidth
                                  required
                                  id={`studyTemplates.[${selectedTempIndex}].tokens.[${tokenIndex}].name`}
                                  name={`studyTemplates.[${selectedTempIndex}].tokens.[${tokenIndex}].value`}
                                  variant="outlined"
                                  size="small"
                                  decimalScale={0}
                                  allowNegative={false}
                                  allowLeadingZeros={false}
                                  value={perTemplate.value}
                                  customInput={MuiTextField}
                                  onBlur={handleBlur}
                                  slotProps={{
                                    htmlInput: {
                                      style: { fontSize: 15 },
                                    },
                                  }}
                                  onValueChange={(values) => {
                                    const newValue = values.floatValue ?? "";

                                    setFieldValue(
                                      `studyTemplates.[${selectedTempIndex}].getStudyTemp`,
                                      true
                                    );

                                    setFieldValue(
                                      `studyTemplates.[${selectedTempIndex}].tokens.[${tokenIndex}].value`,
                                      newValue
                                    );
                                  }}
                                  error={hasError(
                                    selectedTempIndex,
                                    tokenIndex
                                  )}
                                  helperText={
                                    hasError(selectedTempIndex, tokenIndex) &&
                                    t("formValidation.requiredField")
                                  }
                                />
                              ) : perTemplate.type === TokenType.RICH_TEXT ? (
                                <>
                                  <MuiGrid>
                                    <MuiDialog
                                      maxWidth="md"
                                      open={
                                        openDialogIndex?.selectedTempIndex ===
                                        selectedTempIndex &&
                                        openDialogIndex?.tokenIndex ===
                                        tokenIndex
                                      }
                                      onClose={(event, reason) => {
                                        if (reason === "backdropClick") {
                                          // prevent closing on backdrop click
                                          return;
                                        }
                                        setOpenDialogIndex(false);
                                      }}
                                    >
                                      <MuiDialogTitle
                                        sx={{
                                          padding: "10px 24px",
                                        }}
                                      >
                                        <MuiTypography
                                          sx={{
                                            fontSize: "18px",
                                            fontWeight: "bold",
                                          }}
                                        >
                                          {TokensLabelMapping[perTemplate.name]}
                                        </MuiTypography>
                                      </MuiDialogTitle>
                                      <MuiDialogContent
                                        sx={{
                                          marginBottom: "10px",
                                          padding: "0px 15px 10px 15px",
                                          width: "850px",
                                        }}
                                      >
                                        <RichTextEditor
                                          setOpenDialog={setOpenDialogIndex}
                                          tokenIndex={tokenIndex}
                                          perTemp={perTemplate}
                                          selectedTempIndex={selectedTempIndex}
                                          setFieldValue={setFieldValue}
                                        />
                                      </MuiDialogContent>
                                    </MuiDialog>
                                  </MuiGrid>
                                  <MuiGrid>
                                    <MuiButton
                                      variant="contained"
                                      color="primary"
                                      onClick={() =>
                                        setOpenDialogIndex({
                                          selectedTempIndex,
                                          tokenIndex,
                                        })
                                      }
                                    >
                                      {t("templateConfigTable.editToken")}
                                    </MuiButton>
                                  </MuiGrid>
                                </>
                              ) : (
                                <MuiGrid>
                                  <MuiTextField
                                    id={`studyTemplates.[${selectedTempIndex}].tokens.[${tokenIndex}].value`}
                                    name={`studyTemplates.[${selectedTempIndex}].tokens.[${tokenIndex}].value`}
                                    multiline
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    onBlur={handleBlur}
                                    slotProps={{
                                      htmlInput: {
                                        style: { fontSize: 15 },
                                      },
                                    }}
                                    maxRows={4}
                                    value={perTemplate.value}
                                    onChange={(event) => {
                                      const newValue = event.target.value ?? "";
                                      setFieldValue(
                                        `studyTemplates.[${selectedTempIndex}].getStudyTemp`,
                                        true
                                      );

                                      setFieldValue(
                                        `studyTemplates.[${selectedTempIndex}].tokens.[${tokenIndex}].value`,
                                        newValue
                                      );
                                    }}
                                    error={hasError(
                                      selectedTempIndex,
                                      tokenIndex
                                    )}
                                    helperText={
                                      hasError(selectedTempIndex, tokenIndex) &&
                                      t("formValidation.requiredField")
                                    }
                                  />
                                </MuiGrid>
                              )}
                            </MuiGrid>
                          </MuiGrid>
                          <MuiDivider />
                        </MuiGrid>
                      );
                    }
                  )
                ) : (
                  <MuiGrid sx={{ textAlign: "center" }}>
                    {t("templateConfigTable.noTemplateAvailable")}
                  </MuiGrid>
                )}
              </MuiTableBody>
            </MuiTable>
          </MuiTableContainer>
        </MuiPaper>
      </MuiGrid>
    </MuiGrid>
  );
};

export default TemplatesConfiguration;
